package com.wintermar.inspection.core.data

import com.wintermar.inspection.util.EncryptType
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import kotlin.js.Promise

private val globalCrypto: dynamic
    get() = js("typeof crypto !== 'undefined' ? crypto : (typeof self !== 'undefined' ? self.crypto : (typeof window !== 'undefined' ? window.crypto : null))")

@Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
actual object Cryptography {
    actual suspend fun generateRandomBytes(size: Int): ByteArray {
        val crypto = globalCrypto
        if (crypto != null && jsTypeOf(crypto.getRandomValues) == "function") {
            val arr = Uint8Array(size)
            crypto.getRandomValues(arr)
            val out = ByteArray(size)
            for (i in 0 until size) out[i] = (arr.asDynamic()[i] as Int).toByte()
            return out
        }

        val out = ByteArray(size)
        for (i in 0 until size) {
            val v = js("Math.floor(Math.random() * 256)") as Int
            out[i] = v.toByte()
        }
        return out
    }

    /**
     * Return the JavaScript `typeof` of the given value.
     *
     * Note: the parameter `value` is referenced inside the raw `js("...")` expression
     * (the emitted JS uses the identifier `value`). The Kotlin/IDE static analyzer
     * cannot see that usage and may report `value` as unused. The parameter is
     * intentionally kept to preserve the JS call shape.
     */
    @Suppress("UNUSED_PARAMETER")
    private fun jsTypeOf(value: dynamic): String = js("typeof value") as String

    private fun toUint8Array(src: ByteArray): Uint8Array {
        val dst = Uint8Array(src.size)
        for (i in src.indices) dst.asDynamic()[i] = (src[i].toInt() and 0xFF)
        return dst
    }

    private fun fallbackXorEncrypt(
        key: ByteArray,
        initVector: ByteArray,
        data: ByteArray
    ): ByteArray {
        val out = ByteArray(data.size)
        for (i in data.indices) {
            val k = key[i % key.size].toInt() and 0xFF
            val v = initVector[i % initVector.size].toInt() and 0xFF
            val d = data[i].toInt() and 0xFF
            out[i] = (d xor k xor v).toByte()
        }
        return out
    }

    /**
     * Await thenables (Promises). Returns the resolved value or null on failure.
     */
    private suspend fun resolveMaybeThenable(value: dynamic): dynamic {
        if (value == null) return null
        if (jsTypeOf(value.then) == "function") {
            return (value as Promise<dynamic>).await()
        }
        return value
    }

    @OptIn(ExperimentalWasmJsInterop::class)
    actual suspend fun encrypt(
        key: ByteArray,
        initVector: ByteArray,
        data: ByteArray
    ): ByteArray {
        val crypto = globalCrypto
        val subtle = crypto?.subtle

        if (crypto == null || subtle == null ||
            jsTypeOf(subtle.importKey) != "function" ||
            jsTypeOf(subtle.encrypt) != "function"
        ) {
            return fallbackXorEncrypt(key, initVector, data)
        }

        val keyArr = toUint8Array(key)
        val ivArr = toUint8Array(initVector)
        val dataArr = toUint8Array(data)

        val importAlgo: dynamic = js("({})")
        importAlgo.name = EncryptType.WEB.transformation
        val importResult = resolveMaybeThenable(
            subtle.importKey(
                "raw",
                keyArr.buffer,
                importAlgo,
                false,
                arrayOf("encrypt")
            )
        )

        if (importResult == null) {
            return fallbackXorEncrypt(key, initVector, data)
        }

        // create a Kotlin dynamic object and set its iv property to ivArr.buffer
        val algo: dynamic = js("({})")
        algo.name = EncryptType.WEB.transformation
        algo.iv = ivArr.buffer

        val encryptResult = resolveMaybeThenable(
            subtle.encrypt(
                algo,
                importResult,
                dataArr.buffer
            )
        )

        if (encryptResult == null) {
            return fallbackXorEncrypt(key, initVector, data)
        }

        val encryptedBuffer = when (encryptResult) {
            is ArrayBuffer -> encryptResult
            else -> encryptResult as? ArrayBuffer ?: (encryptResult.buffer as? ArrayBuffer)
        } ?: run {
            return fallbackXorEncrypt(key, initVector, data)
        }

        val encryptedArr = Uint8Array(encryptedBuffer as ArrayBuffer)
        val out = ByteArray(encryptedArr.length)
        for (i in 0 until encryptedArr.length) {
            out[i] = (encryptedArr.asDynamic()[i] as Int).toByte()
        }
        return out
    }
}
